import { GetStaticPropsContext, NextPage } from 'next'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLockedBody } from 'usehooks-ts'
import dynamic from 'next/dynamic'

import {
  GetStaticPropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import { Offer } from '../../rest/types/Offer'
import useFooter from '../../relay/Footer/useFooter'
import getConfiguration from '../../rest/services/getConfiguration'
import RestApiClient from '../../rest/client'
import { initWithLangAndLabels } from '../../i18n'
import OfferTemplate, {
  OfferTemplateProps,
} from '../../templates/OfferTemplate'
import offerBySlug from '../../rest/requests/offers/offerBySlug'
import { BreadcrumbTransformer } from '../../relay/Transformers/BreadcrumbTransformer'
import { selectors } from '../../redux'
import useAddFeedbackProps from '../../relay/Modals/useAddFeedbackProps'
import useOfferBannerProps from '../../relay/Offer/useOfferBannerProps'
import { ImageTransformer } from '../../relay/Transformers/ImageTransformer'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'
import { DownloadTransformer } from '../../relay/Transformers/DownloadTransformer'
import useCardProps from '../../relay/Offer/useCardProps'
import useFeedbackProps from '../../relay/Offer/useFeedbackProps'
import useHeaderBis from '../../relay/header/useHeaderBis'
import { PageType } from '../../rest/types/Page'
import useLoginModal from '../../relay/Modals/useLoginModal'
import tracking from '../../tracking'

const AddFeedbackModal = dynamic(
  () => import('../../components/AddFeedbackModal')
)

const LoginModal = dynamic(() => import('../../components/LoginModal'))

type Props = PageProps & {
  data: Offer
}

const OfferPage: NextPage<Props> = ({ data, configuration }) => {
  const headerProps = useHeaderBis(configuration, PageType.Store)
  const footerProps = useFooter(configuration)
  const isLogin = useSelector(selectors.auth.isConnected)
  const isMember = useSelector(selectors.auth.isSubscriptionActive)
  const hasAccessToStore = useSelector(selectors.auth.hasAccessToStore)
  const loginModalProps = useLoginModal()
  const initialDataRef = useRef(data)
  const user = useSelector(selectors.auth.user)
  const isInit = useSelector(selectors.app.isInit)
  const meIsLoading = useSelector(selectors.auth.meIsLoading)

  const [isAddFeedbackModalVisible, setIsAddFeedbackModalVisible] =
    useState<boolean>(false)

  const [_locked, setLocked] = useLockedBody(false, 'root')
  const { t } = useTranslation()

  useEffect(() => {
    if (isInit && !meIsLoading) {
      tracking.offer_view(initialDataRef.current, user)
    }
  }, [user, isInit, meIsLoading])

  const addFeedbackProps = useAddFeedbackProps(
    isAddFeedbackModalVisible,
    setIsAddFeedbackModalVisible,
    setLocked,
    data.id,
    data?.slug,
    configuration
  )

  const props: OfferTemplateProps = {
    breadcrumb: BreadcrumbTransformer(data?.categories, t),
    productBanner: {
      offerLink: {
        text: data?.name,
      },
      description: data?.shortDescription ?? '',
      image: ImageTransformer(data?.image as any),
      ...(data?.link?.href && {
        webSiteLinkButton: {
          text: data?.link?.text,
          href: data?.link?.href,
          target: data?.link?.target ?? '_self',
          variant: data?.link?.variant,
        },
      }),
      ...(data?.secondLink?.href &&
        isMember && {
          accountCreationLinkButton: {
            text: data?.secondLink?.text,
            href: data?.secondLink?.href,
            target: data?.secondLink?.target ?? '_self',
            variant: data?.secondLink?.variant,
          },
        }),
      stockTextInfo:
        parseInt(data?.stock) >= 1 ? undefined : t('no_available_stock'),
    },

    offerBanner: useOfferBannerProps(data),

    offerDescription:
      isLogin && hasAccessToStore
        ? isMember
          ? data?.privateDescription
          : data.connectedDescription
        : data?.publicDescription,
    ...(isLogin &&
      hasAccessToStore &&
      isMember &&
      data?.downloads && {
        downloadFile: DownloadTransformer({ data: data?.downloads, t }),
      }),
    brandCard: useCardProps({
      data: data?.brand as any,
      typeOfCard: 'brand',
    }),
    retailerCard: useCardProps({
      data: data?.wholesaler as any,
      typeOfCard: 'retailer',
    }),
    memberFeedback: useFeedbackProps(
      data?.id,
      setIsAddFeedbackModalVisible,
      setLocked,
      configuration
    ),
    modals: (
      <>
        <AddFeedbackModal {...addFeedbackProps} />
        <LoginModal {...loginModalProps} />
      </>
    ),

    headerProps,
    footerProps,
  }
  return <OfferTemplate {...props} />
}

export default OfferPage

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export async function getStaticProps(
  context: GetStaticPropsContext
): GetStaticPropsReturnType<Props> {
  const slug: string = String(context?.params?.slug ?? '')

  const [configuration, rs] = await Promise.all([
    getConfiguration(),
    RestApiClient.call(offerBySlug({ slug })),
  ])

  const data = rs?.data
  const i18n = initWithLangAndLabels('fr', configuration.translations)

  if (!data) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
      revalidate: 30,
    }
  }

  return {
    props: {
      data,
      metas: SeoTransformer(
        Router.getRouteUrl(routes.offer, context?.params),
        data?.seo
      ),
      configuration,
      i18n,
    },
    revalidate: 30,
  }
}
